import { Modal, notification } from 'antd';
import { PureComponent, ReactNode } from 'react';
import getPackagePanelDetails, { Data } from '../../../../../api/getPackagePanelDetails';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { LabTestsEntityOrProceduresEntity } from '../../../../../store/types/prescription';
import { track } from '../../../../../tracking';
import { getVendorLogo } from '../../finishScreen/BookLabTestsUtils';
import { getMySingleDoctorId } from '../../../../../store/idbHandler';

type Props = {
    activeLabTestElement?: LabTestsEntityOrProceduresEntity;
    closeMe: () => void;
};

type State = {
    isFetchingDetails?: boolean;
    data: Data[];
};

class LabTestsIncludedInPackagePanel extends PureComponent<Props, State> {
    state: State = { data: [] };

    componentDidUpdate = async (prevProps: Props): Promise<void> => {
        const { activeLabTestElement } = this.props;
        const docId = getMySingleDoctorId() || '';
        if (!activeLabTestElement) return;
        if (prevProps.activeLabTestElement !== this.props.activeLabTestElement) {
            const { id } = activeLabTestElement;
            this.setState({
                isFetchingDetails: true,
                data: [],
            });
            try {
                const { success, data = [], error } = await getPackagePanelDetails(docId, id);
                if (success) {
                    this.setState({
                        data,
                    });
                } else if (error) {
                    captureErrorString(error);
                    track('ERROR', {
                        on: 'getPackagePanelDetails',
                        error,
                    });
                    notification.error({
                        message: 'Error while fetching details!',
                        duration: 5,
                    });
                }
            } catch (error) {
                captureError(error as Error);
                track('ERROR', {
                    on: 'getPackagePanelDetails',
                    error: (error as Error).message,
                });
                notification.error({
                    message: 'Error while fetching details!',
                    duration: 5,
                });
            } finally {
                this.setState({
                    isFetchingDetails: false,
                });
            }
        }
    };

    render(): ReactNode {
        const { activeLabTestElement, closeMe } = this.props;
        const { data, isFetchingDetails } = this.state;

        return (
            <Modal
                bodyStyle={{
                    padding: 0,
                }}
                title={
                    <div className="flex items-center justify-between text-darwin-neutral-900 w-full">
                        <p>
                            <span className="font-700">
                                “{activeLabTestElement?.name || 'Package / Panel'}”
                            </span>{' '}
                            Inclusions
                        </p>
                        <svg
                            className="w-14 h-14 fill-darwin-neutral-900 cursor-pointer"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={closeMe}
                        >
                            <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                        </svg>
                    </div>
                }
                visible={Boolean(activeLabTestElement)}
                onCancel={closeMe}
                footer={null}
                closable={false}
                destroyOnClose
                width={Math.max(500, data.length * 380)}
            >
                {isFetchingDetails ? (
                    <div className="h-328 flex items-center justify-center space-x-10 pb-16">
                        <svg
                            className="w-18 h-18 fill-darwin-primary-500 animate-spin"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                        <p className="text-darwin-primary-500 font-700">
                            Fetching package / panel details...
                        </p>
                    </div>
                ) : data.length ? (
                    <div className="m-16 overflow-scroll flex items-start border border-darwin-neutral-100">
                        {data.map((i, index) => (
                            <div
                                className={`flex-1 ${
                                    index + 1 !== data.length ? 'border-r' : ''
                                } border-darwin-neutral-100 min-w-360`}
                            >
                                <div className="w-full flex items-center space-x-10 px-16 py-12 border-b border-darwin-neutral-100 bg-darwin-neutral-50">
                                    <p className="font-700">{index + 1}.</p>
                                    <p className="font-700"> Provided by:</p>
                                    <img
                                        src={getVendorLogo(i.vendor_id)}
                                        alt={i.vendor_id}
                                        className="h-26"
                                    />
                                </div>
                                <div className="w-full space-y-4 select-none overflow-auto max-h-360 px-16 py-12">
                                    {i.included_tests.map((test, index) => (
                                        <p className="flex items-start space-x-8">
                                            <p>{index + 1}.</p>
                                            <p>{test.name}</p>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="h-328 flex items-center justify-center space-x-10 pb-16">
                        <svg
                            className="w-18 h-18 fill-darwin-status-warning-500"
                            viewBox="0 0 18 14"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M9 0C9.4375 0 9.84375 0.25 10.0625 0.625L16.8125 12.125C17.0312 12.5312 17.0312 13 16.8125 13.375C16.5938 13.7812 16.1875 14 15.75 14H2.25C1.78125 14 1.375 13.7812 1.15625 13.375C0.9375 13 0.9375 12.5312 1.15625 12.125L7.90625 0.625C8.125 0.25 8.53125 0 9 0ZM9 4C8.5625 4 8.25 4.34375 8.25 4.75V8.25C8.25 8.6875 8.5625 9 9 9C9.40625 9 9.75 8.6875 9.75 8.25V4.75C9.75 4.34375 9.40625 4 9 4ZM10 11C10 10.4688 9.53125 10 9 10C8.4375 10 8 10.4688 8 11C8 11.5625 8.4375 12 9 12C9.53125 12 10 11.5625 10 11Z" />
                        </svg>
                        <p className="text-darwin-status-warning-500 font-700">
                            No Data Available!
                        </p>
                    </div>
                )}
            </Modal>
        );
    }
}

export default LabTestsIncludedInPackagePanel;
